import React from 'react';
import { Link } from 'react-router-dom';
import { useCaseFileStyle } from '../../context/CaseFileStyleContext';

interface CaseFileHeroProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  totalOfficials: number | null;
  totalAdoptions: number | null;
  dossiersCompleted: number | null;
}

/**
 * Homepage hero, restyled per the case-file mockup's two directions. The
 * mockup's hero is a ZIP-address lookup, but the real app only supports
 * name/office/state search — no ZIP-to-district backend exists — so this
 * keeps the real search behavior and applies the case-file visual language
 * to it, rather than fabricating a ZIP feature.
 */
export const CaseFileHero: React.FC<CaseFileHeroProps> = ({
  searchTerm,
  onSearchTermChange,
  onSubmit,
  totalOfficials,
  totalAdoptions,
  dossiersCompleted,
}) => {
  const { style } = useCaseFileStyle();

  // Both directions render as a fixed paper tone (kraft tan / cream ruled-grid)
  // regardless of site dark mode — like a physical document under a lamp,
  // not a UI panel that inverts. That's a deliberate choice, not an oversight:
  // it avoids re-deriving every text color for a dark variant, and it's true
  // to what the mockup is depicting (paper doesn't go dark in a dark room).
  if (style === 'evidence-board') {
    return (
      <section className="bg-kraft-texture rounded-xl border border-navy-900/15 px-6 sm:px-10 py-10 sm:py-12 relative overflow-hidden">
        <div className="absolute top-4 right-6 border-[3px] border-red-600/50 text-red-600/60 px-3 py-1.5 -rotate-6 font-black text-[11px] uppercase tracking-[0.2em]">
          Public Record
        </div>
        <div className="flex items-center gap-3 mb-5 max-w-xl">
          <span className="bg-navy-900 text-gold-500 px-3 py-1.5 font-bold text-[9px] uppercase tracking-[0.24em] mono shrink-0">
            Case Intake · Step 01
          </span>
          <span className="flex-1 h-px bg-navy-900/20" />
          <span className="font-bold text-[9px] text-navy-500 uppercase tracking-[0.16em] mono shrink-0">
            {totalOfficials !== null ? `${totalOfficials.toLocaleString()} records live` : '...'}
          </span>
        </div>
        <h1 className="font-black text-4xl sm:text-6xl text-navy-900 uppercase leading-[0.9] tracking-tight mb-4 max-w-2xl">
          Every official <span className="text-red-600">on the record</span>.
        </h1>
        <p className="text-navy-800 max-w-xl mb-6 leading-relaxed">
          Search any elected official by name, office, or state. Their record, reversals and receipts, already pulled.
        </p>
        <form onSubmit={onSubmit} className="flex max-w-xl shadow-[0_3px_0_rgba(15,36,64,.25)]">
          <div className="flex-1 bg-cream-50 border-2 border-navy-900 border-r-0 px-4 py-3">
            <div className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-1 mono">Field 1 — Name, office, or state</div>
            <input
              type="text"
              placeholder="e.g. Marcus Fain, Governor, Ohio"
              value={searchTerm}
              onChange={(e) => onSearchTermChange(e.target.value)}
              className="w-full bg-transparent text-navy-900 font-bold text-lg mono outline-none placeholder:text-navy-300"
            />
          </div>
          <button type="submit" className="bg-red-600 hover:bg-red-700 text-cream-50 border-2 border-navy-900 px-6 font-black text-sm uppercase tracking-wide transition">
            Pull Files
          </button>
        </form>
        <p className="mt-3 text-[10px] font-bold text-navy-800 uppercase tracking-[0.12em] mono">
          <Link to="/officials" className="underline hover:text-red-600 transition">Browse all {totalOfficials !== null ? totalOfficials.toLocaleString() : ''} officials</Link> · No account needed to look
        </p>
      </section>
    );
  }

  // field-report
  return (
    <section className="bg-ruled-grid rounded-xl border border-navy-900/15 px-6 sm:px-10 py-10 sm:py-12">
      <div className="flex justify-between items-baseline font-bold text-[9px] text-navy-400 uppercase tracking-[0.2em] border-b border-navy-900 pb-2.5 mb-7 mono">
        <span>Section 1 — Officials registry</span>
        <span>{totalOfficials !== null ? `${totalOfficials.toLocaleString()} records live` : '...'}</span>
      </div>
      <h1 className="font-black text-4xl sm:text-6xl text-navy-900 uppercase leading-[0.9] tracking-tight mb-4 max-w-2xl">
        Find out who <span className="border-b-[6px] border-gold-500">answers</span> to you.
      </h1>
      <p className="text-navy-600 max-w-xl mb-7 leading-relaxed">
        Search by name, office, or state. We return the accountability score and what they did last month.
      </p>
      <form onSubmit={onSubmit} className="border-2 border-navy-900 bg-cream-50 max-w-xl flex items-stretch">
        <div className="bg-navy-900 text-cream-50 px-3.5 flex items-center font-bold text-[10px] uppercase tracking-[0.16em] mono">1a</div>
        <div className="flex-1 px-4 py-3 border-r border-cream-300">
          <div className="font-bold text-[8px] text-navy-400 uppercase tracking-[0.2em] mb-1 mono">Name, office, or state</div>
          <input
            type="text"
            placeholder="e.g. Marcus Fain, Governor, Ohio"
            value={searchTerm}
            onChange={(e) => onSearchTermChange(e.target.value)}
            className="w-full bg-transparent text-navy-900 font-bold text-lg mono outline-none placeholder:text-navy-300"
          />
        </div>
        <button type="submit" className="bg-navy-900 hover:opacity-90 text-cream-50 px-7 font-black text-xs uppercase tracking-wide transition">
          Submit
        </button>
      </form>
      <p className="mt-5 pt-4 border-t border-dashed border-cream-400 max-w-xl text-[10px] font-bold text-navy-400 uppercase tracking-[0.12em] mono">
        <Link to="/officials" className="text-gold-600 hover:underline">Browse full registry →</Link>
      </p>
    </section>
  );
};
